import { Suspense, useEffect, useState } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { useDispatch } from "react-redux";
import CLoading from "../../components/common/CLoading";
import RouterArr from "./router";
import { isUserAdmin } from "@/utils/jwtHelper";
import { isAuthenticated, tokenSubject, ACCESS_KEY } from "@/api/HttpClient";
import { useSelectorRoot } from "@/store/hooks";
import { ConnectionState, RabbitMqLog, startWorkflowConnection, stopWorkflowConnection, WorkflowStatusUpdate } from "@/services/SignalRWorkflowService";
import { workflowActions, SignalRWorkflowMessage } from "@/store/workflow/workflowSlice";

const renderRoutes = (routes: any[], authed: boolean) =>
  routes.map(({ path, component: Component, auth, requireAdmin, children, ...rest }) => {
    // Check admin permission if required
    if (requireAdmin && !isUserAdmin()) {
      return (
        <Route 
          path={path} 
          element={<Navigate to="/page/drive" replace />} 
          key={path || "root"} 
          {...rest}
        />
      );
    }

    const element = auth ? (
      authed ? <Component /> : <Navigate to="/auth/login" replace />
    ) : (
      <Component />
    );

    return (
      <Route path={path} element={element} key={path || "root"} {...rest}>
        {children && renderRoutes(children, authed)}
      </Route>
    );
  });

/**
 * Transform WorkflowStatusUpdate from SignalR to SignalRWorkflowMessage for Redux
 */
function transformSignalRUpdate(update: WorkflowStatusUpdate): SignalRWorkflowMessage | null {
  const { raw, status, workflow_id } = update;
  
  if (!workflow_id || !status) {
    return null;
  }

  // Determine category and event_type from status
  const statusLower = status.toLowerCase();
  
  let category: "task" | "workflow" = "workflow";
  let event_type: SignalRWorkflowMessage["event_type"];

  // Map status to event_type
  if (statusLower.includes("task")) {
    category = "task";
    if (statusLower.includes("completed")) {
      event_type = "task_completed";
    } else if (statusLower.includes("failed")) {
      event_type = "task_failed";
    } else if (statusLower.includes("started") || statusLower.includes("running")) {
      event_type = "task_started";
    } else {
      event_type = "task_started"; // default
    }
  } else {
    // workflow category
    if (statusLower.includes("completed") || statusLower === "completed") {
      event_type = "workflow_completed";
    } else if (statusLower.includes("failed") || statusLower === "failed") {
      event_type = "workflow_failed";
    } else if (statusLower.includes("started") || statusLower.includes("running") || statusLower === "started") {
      event_type = "workflow_started";
    } else {
      event_type = "workflow_started"; // default for "Created" or others
    }
  }

  // Build SignalRWorkflowMessage
  const message: SignalRWorkflowMessage = {
    category,
    event_type,
    id: raw.id || workflow_id,
    workflow_id,
    task_id: raw.task_id || (raw as any).taskId,
    task_name: raw.task_name || (raw as any).taskName,
    queue: raw.queue,
    timestamp: raw.timestamp || new Date().toISOString(),
    monitoring_metadata: raw.monitoring_metadata as any,
    signalr_metadata: raw.signalr_metadata as any,
    _id: raw._id,
    _workflow_group: raw._workflow_group,
  };

  return message;
}

const MainRouter = (): React.ReactElement => {
  const dispatch = useDispatch();
  const [authed, setAuthed] = useState(isAuthenticated());
  const flowStatus = useSelectorRoot((state) => state.drive.flowStatus);

  useEffect(() => {
    const sub = tokenSubject.subscribe((token) => {
      setAuthed(!!token);
    });
    return () => sub.unsubscribe();
  }, []);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ACCESS_KEY && e.newValue === null) {
        console.log("Token removed in another tab, logging out...");
        tokenSubject.next(null);
        window.location.href = "/login";
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // ===== Realtime workflow status via SignalR =====
  useEffect(() => {
    const handleUpdate = (update: RabbitMqLog) => {
      // Transform and dispatch to Redux
  
      if (update) {
        dispatch(workflowActions.receiveSignalRMessage(update));
      }
    };

    const handleConnState = (state: ConnectionState) => {
      console.log("[SignalR Workflow] Connection state:", state);
    };

    startWorkflowConnection(handleUpdate, handleConnState);

    return () => {
      stopWorkflowConnection();
    };
  }, [dispatch]);

  return (
    <Router>
      <Suspense fallback={<CLoading visible={true} fullScreen={true} />}>
        <Routes>
          {authed ? (
            <>
              <Route path="/" element={<Navigate to="/page/drive" replace />} />
              {/* Don't auto-redirect from login until drive flow is ready */}
              <Route path="/auth/login" element={
                flowStatus === "ready" ? <Navigate to="/page/drive" replace /> : <CLoading visible={true} fullScreen={true} />
              } />
              {renderRoutes(RouterArr, authed)}
            </>
          ) : (
            <>
            <Route path="*" element={<Navigate to="/auth/login" replace />} />
            {renderRoutes(
                RouterArr.filter((r) => r.path.startsWith("/auth")),
                authed
              )}
            </>
          )}
        </Routes>
      </Suspense>
    </Router>
  );
};

export default MainRouter;
